import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Trash2, SlidersHorizontal } from "lucide-react";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { API, auth, inputClass, labelClass, fileClass, primaryBtn, handleAuthError } from "./shared";
import { useConfirm } from "./useConfirm";

const AdminBeforeAfter = ({ token, logout }) => {
  const [items, setItems] = useState([]);
  const [before, setBefore] = useState(null);
  const [after, setAfter] = useState(null);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const { confirm, dialog } = useConfirm();

  const load = () => axios.get(`${API}/before-after`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!before || !after) return toast.error("Choisissez la photo AVANT et la photo APRÈS");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("before", before);
      fd.append("after", after);
      fd.append("caption", caption);
      await axios.post(`${API}/before-after`, fd, auth(token));
      toast.success("Comparaison avant / après publiée !");
      setBefore(null);
      setAfter(null);
      setCaption("");
      document.getElementById("admin-before-input").value = "";
      document.getElementById("admin-after-input").value = "";
      load();
    } catch (err) {
      handleAuthError(err, logout, () => toast.error("Échec de l'envoi. Réessayez."));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (item) => {
    const ok = await confirm({ title: "Supprimer cette comparaison ?", description: `« ${item.caption || "Sans légende"} » disparaîtra de la galerie et de l'accueil.` });
    if (!ok) return;
    try {
      await axios.delete(`${API}/before-after/${item.id}`, auth(token));
      toast.success("Comparaison supprimée");
      load();
    } catch (err) {
      handleAuthError(err, logout, () => toast.error("Échec de la suppression"));
    }
  };

  return (
    <div data-testid="admin-before-after-tab">
      {dialog}
      <form onSubmit={upload} className="rounded-3xl bg-cream p-8" data-testid="admin-before-after-form">
        <h2 className="font-display flex items-center gap-2 text-lg font-bold text-forest">
          <SlidersHorizontal className="h-5 w-5 text-gold" />
          Ajouter une comparaison avant / après
        </h2>
        <p className="mt-2 text-sm text-brand-hover">Deux photos du même véhicule, prises sous le même angle pour un effet optimal.</p>
        <div className="mt-6 grid gap-5 sm:grid-cols-3">
          <div>
            <label className={labelClass}>Photo AVANT *</label>
            <input id="admin-before-input" type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setBefore(e.target.files[0])} data-testid="admin-before-file-input" className={fileClass} />
          </div>
          <div>
            <label className={labelClass}>Photo APRÈS *</label>
            <input id="admin-after-input" type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setAfter(e.target.files[0])} data-testid="admin-after-file-input" className={fileClass} />
          </div>
          <div>
            <label className={labelClass}>Légende</label>
            <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Ex : Toyota Prado — carrosserie & peinture" data-testid="admin-before-after-caption-input" className={inputClass} />
          </div>
        </div>
        <button type="submit" disabled={uploading} data-testid="admin-before-after-upload-button" className={`mt-6 ${primaryBtn}`}>
          <Upload className="h-4 w-4" />
          {uploading ? "Envoi en cours..." : "Publier la comparaison"}
        </button>
      </form>

      <h2 className="font-display mt-14 text-lg font-bold text-forest">
        Comparaisons en ligne <span className="text-gold">({items.length})</span>
      </h2>
      <div className="mt-6 grid gap-8 md:grid-cols-2">
        {items.map((item, i) => (
          <div key={item.id} className="relative" data-testid={`admin-before-after-item-${i}`}>
            <BeforeAfterSlider item={item} labels={{ before: "Avant", after: "Après" }} testid={`admin-before-after-slider-${i}`} />
            <button
              onClick={() => remove(item)}
              data-testid={`admin-before-after-delete-${i}`}
              className="mt-3 inline-flex items-center gap-2 rounded-full border border-brand/30 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-brand transition-colors hover:border-gold hover:text-gold"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Supprimer
            </button>
          </div>
        ))}
        {items.length === 0 && (
          <div className="col-span-full rounded-2xl border-2 border-dashed border-brand/20 px-6 py-10 text-brand">
            Aucune comparaison pour le moment.
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBeforeAfter;
